import React, { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { APIContext } from "../../api";
import HelmetLayout from "../../components/HelmetLayout";

const inputClass =
  "w-full rounded-lg border border-slate-300 px-3 py-2 bg-white text-slate-900";
const buttonClass =
  "rounded-lg bg-[#623ce7] text-white px-4 py-2 disabled:opacity-50";
const money = (terms) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: terms.currency,
  }).format(terms.amount / 100);
export default function EnterprisePlans() {
  const { adminActions } = useContext(APIContext);
  const [params] = useSearchParams();
  const [offers, setOffers] = useState([]),
    [fields, setFields] = useState({});
  const [form, setForm] = useState({
    user_id: params.get("user") || "",
    name: "Grigora Enterprise",
    description: "",
    price: "",
    interval: "month",
    expires_days: 30,
    limits: {},
  });
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const call = (action, data = {}) =>
    adminActions(`enterprise-plans/${action}`, data);
  async function load() {
    const data = await call("list", { user_id: params.get("user") || "" });
    setOffers(data.offers);
    setFields(data.fields);
    setForm((old) => ({
      ...old,
      limits: Object.keys(old.limits).length ? old.limits : data.defaults,
    }));
  }
  useEffect(() => {
    load().catch((e) =>
      setError(e?.response?.data?.message || "Could not load custom plans.")
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  async function run(work) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await work();
      await load();
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e.message ||
          "The operation could not be completed."
      );
    } finally {
      setBusy(false);
    }
  }
  const update = (key, value) => setForm((old) => ({ ...old, [key]: value }));
  const updateLimit = (key, value) =>
    setForm((old) => ({ ...old, limits: { ...old.limits, [key]: value } }));
  function duplicate(offer) {
    setForm({
      user_id: offer.user_id,
      ...offer.terms,
      price: (offer.terms.amount / 100).toFixed(2),
      expires_days: 30,
    });
    setNotice(
      "Offer copied into the form. Saving creates a new contract; the existing subscription keeps its agreed terms."
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  return (
    <div className="max-w-6xl space-y-6 pb-16">
      <HelmetLayout title="Custom Plans" />
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Custom Plans</h1>
        <p className="text-sm text-slate-500 mt-1">
          Create an account-specific offer, review it, then publish a payment
          link. Payment activates its limits automatically.
        </p>
      </div>
      {error && (
        <div
          role="alert"
          className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg"
        >
          {error}
        </div>
      )}
      {notice && (
        <div
          role="status"
          className="bg-indigo-50 text-indigo-900 p-4 rounded-lg"
        >
          {notice}
        </div>
      )}
      <form
        className="bg-white border border-slate-200 rounded-xl p-6 space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          run(async () => {
            if (!/^\d+(\.\d{1,2})?$/.test(form.price))
              throw new Error("Enter a price with at most two decimal places.");
            await call("create", {
              payload: JSON.stringify({
                ...form,
                amount: Math.round(Number(form.price) * 100),
              }),
            });
            setNotice(
              "Draft created. Review the resolved account email and terms below before publishing."
            );
          });
        }}
      >
        <h2 className="font-semibold text-lg">New offer</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <label className="text-sm space-y-1">
            <span>Grigora account ID</span>
            <input
              required
              className={inputClass}
              value={form.user_id}
              onChange={(e) => update("user_id", e.target.value)}
            />
            <span className="block text-xs text-slate-500">
              Open Custom Plan from the customer’s user page to select their
              account.
            </span>
          </label>
          <label className="text-sm space-y-1">
            <span>Plan name</span>
            <input
              required
              minLength={3}
              maxLength={64}
              className={inputClass}
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
            />
          </label>
          <label className="text-sm space-y-1">
            <span>Price (USD)</span>
            <input
              required
              inputMode="decimal"
              className={inputClass}
              value={form.price}
              onChange={(e) => update("price", e.target.value)}
              placeholder="99.00"
            />
          </label>
          <label className="text-sm space-y-1">
            <span>Billing interval</span>
            <select
              className={inputClass}
              value={form.interval}
              onChange={(e) => update("interval", e.target.value)}
            >
              <option value="month">Monthly</option>
              <option value="year">Yearly</option>
            </select>
          </label>
          <label className="text-sm space-y-1">
            <span>Offer valid for (days)</span>
            <input
              required
              type="number"
              min="1"
              max="365"
              className={inputClass}
              value={form.expires_days}
              onChange={(e) => update("expires_days", Number(e.target.value))}
            />
          </label>
        </div>
        <label className="block text-sm space-y-1">
          <span>Customer-facing description</span>
          <textarea
            className={inputClass}
            maxLength={2000}
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
          />
        </label>
        <h3 className="font-semibold">Included limits and features</h3>
        <p className="text-sm text-slate-500">
          AI credits reset each calendar month, including on yearly plans.
          Existing paid add-ons remain additive. Shared email delivery
          safeguards still apply.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(fields).map(([key, field]) => {
            const value = form.limits[key];
            if (typeof field.default === "boolean")
              return (
                <label key={key} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={!!value}
                    onChange={(e) => updateLimit(key, e.target.checked)}
                  />
                  {field.label}
                </label>
              );
            const bytes = ["storage", "file_upload_size"].includes(key),
              divisor = bytes ? 1073741824 : 1;
            return (
              <label key={key} className="text-sm space-y-1">
                <span>
                  {bytes
                    ? field.label.replace("(bytes)", "(GiB)")
                    : field.label}
                </span>
                <input
                  required
                  type="number"
                  min="0"
                  step={bytes ? "any" : "1"}
                  className={inputClass}
                  value={value == null ? "" : value / divisor}
                  onChange={(e) =>
                    updateLimit(key, Number(e.target.value) * divisor)
                  }
                />
              </label>
            );
          })}
        </div>
        <button
          disabled={busy || !Object.keys(fields).length}
          className={buttonClass}
        >
          Save draft for review
        </button>
      </form>
      <div className="flex justify-between items-center">
        <h2 className="font-semibold text-xl">Offers</h2>
        <button
          disabled={busy}
          onClick={() => run(load)}
          className="text-sm text-indigo-700"
        >
          Refresh
        </button>
      </div>
      {offers.length === 0 && (
        <p className="text-slate-500">No custom offers yet.</p>
      )}
      {offers.map((offer) => (
        <article
          key={offer.id}
          className="bg-white rounded-xl border border-slate-200 p-6 space-y-4"
        >
          <div className="flex flex-wrap justify-between gap-3">
            <div>
              <h3 className="font-semibold text-lg">{offer.terms.name}</h3>
              <p className="text-sm text-slate-600">{offer.email}</p>
              <p className="text-xs text-slate-500">{offer.user_id}</p>
            </div>
            <div className="text-right">
              <strong>
                {money(offer.terms)} / {offer.terms.interval}
              </strong>
              <p className="text-sm capitalize">
                {offer.state} · expires{" "}
                {new Date(offer.expires_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          <p className="text-sm">{offer.terms.description}</p>
          <details>
            <summary className="cursor-pointer text-sm font-medium">
              Review agreed limits
            </summary>
            <dl className="grid sm:grid-cols-2 gap-2 mt-3 text-sm">
              {Object.entries(offer.terms.limits).map(([key, value]) => (
                <div
                  key={key}
                  className="flex justify-between gap-4 border-b pb-1"
                >
                  <dt>{fields[key]?.label || key}</dt>
                  <dd>
                    {typeof value === "boolean"
                      ? value
                        ? "Included"
                        : "Not included"
                      : value.toLocaleString()}
                  </dd>
                </div>
              ))}
            </dl>
          </details>
          {offer.link && (
            <label className="block text-sm">
              Account-bound payment link
              <input
                readOnly
                className={`${inputClass} mt-1`}
                value={offer.link}
                onFocus={(e) => e.target.select()}
              />
            </label>
          )}
          {offer.subscriptions?.map((s) => (
            <p key={s.subscription_id} className="text-sm text-slate-600">
              Subscription: {s.status}
              {s.cancel_at_period_end ? " · cancellation scheduled" : ""}
              {s.period_end
                ? ` · period ends ${new Date(
                    s.period_end
                  ).toLocaleDateString()}`
                : ""}
            </p>
          ))}
          <div className="flex flex-wrap gap-3 text-sm">
            {["draft", "publishing"].includes(offer.state) && (
              <button
                disabled={busy}
                className={buttonClass}
                onClick={() =>
                  run(async () => {
                    await call("publish", { id: offer.id });
                    setNotice(
                      "Offer published. Copy its link and share it with the selected customer."
                    );
                  })
                }
              >
                {offer.state === "publishing"
                  ? "Recover publishing"
                  : "Publish payment link"}
              </button>
            )}
            {offer.product_id && (
              <button
                disabled={busy}
                className="border rounded-lg px-3 py-2"
                onClick={() =>
                  run(async () => {
                    await call("reconcile", { id: offer.id });
                    setNotice("Billing state reconciled with Polar.");
                  })
                }
              >
                Sync billing status
              </button>
            )}
            <button
              disabled={busy}
              className="border rounded-lg px-3 py-2"
              onClick={() => duplicate(offer)}
            >
              Create revised offer
            </button>
            {offer.state !== "withdrawn" && (
              <button
                disabled={busy}
                className="text-red-700 px-3 py-2"
                onClick={() =>
                  run(async () => {
                    await call("withdraw", { id: offer.id });
                    setNotice(
                      "Offer withdrawn. Existing subscriptions retain their agreed terms."
                    );
                  })
                }
              >
                Withdraw offer
              </button>
            )}
          </div>
        </article>
      ))}
    </div>
  );
}
